import React, { useEffect, useState, useMemo } from 'react';
import { Box, Tabs, Tab, Typography, Alert } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { McDrawer } from '@maersk-global/mds-react-wrapper/components-core/mc-drawer';
import { McButton } from '@maersk-global/mds-react-wrapper/components-core/mc-button';
import { IncidentCard } from './IncidentsDrawer';
import { MAERSK_FONT_FAMILY } from '../constants';

const maerskFontFamily = MAERSK_FONT_FAMILY;

const DEFAULT_LABEL_MAP = {
  p1: 'P1',
  p2: 'P2',
  p3: 'P3',
  p4: 'P4'
};

const IncidentsOverviewDrawer = ({
  open,
  onClose,
  incidentsDetail = {},
  incidentCounts = {},
  incidentLabelMap = DEFAULT_LABEL_MAP,
  initialTab = 'p1'
}) => {
  const severityKeys = useMemo(() => {
    const keys = Object.keys(incidentLabelMap);
    return keys.length ? keys : Object.keys(DEFAULT_LABEL_MAP);
  }, [incidentLabelMap]);

  const [activeTab, setActiveTab] = useState(() => {
    return severityKeys.includes(initialTab) ? initialTab : severityKeys[0];
  });

  useEffect(() => {
    if (open) {
      setActiveTab(severityKeys.includes(initialTab) ? initialTab : severityKeys[0]);
    }
  }, [initialTab, open, severityKeys]);

  const incidentKey = `${activeTab}_incidents`;
  const currentIncidents = incidentsDetail?.[incidentKey] || [];
  const totalForTab = incidentCounts[activeTab] ?? currentIncidents.length;

  const hasUnidentifiedChange = currentIncidents.some(
    (incident) =>
      incident.change_number &&
      incident.change_number.startsWith('CHG') &&
      incident.change_number.includes('X')
  );

  return (
    <McDrawer open={open} closed={onClose}>
      <span slot="heading" style={{ color: '#0073ab', fontFamily: maerskFontFamily }}>
        Incidents due to Changes
      </span>
      <Box sx={{ mt: 1, pr: 1 }}>
        <Tabs
          value={activeTab}
          onChange={(_event, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontFamily: maerskFontFamily
            }
          }}
        >
          {severityKeys.map((severity) => (
            <Tab
              key={severity}
              value={severity}
              label={`${incidentLabelMap[severity] || severity.toUpperCase()} (${incidentCounts[severity] || 0})`}
            />
          ))}
        </Tabs>

        {hasUnidentifiedChange && (
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon fontSize="small" />}
            sx={{
              mt: 1.5,
              fontFamily: maerskFontFamily,
              backgroundColor: '#e3f2fd',
              '& .MuiAlert-message': {
                fontSize: '0.875rem'
              }
            }}
          >
            CHGXXXXXXX indicates that the triggering change request is not yet identified
          </Alert>
        )}

        <Typography
          sx={{
            mt: 2,
            mb: 1,
            fontWeight: 600,
            fontSize: '0.95rem',
            color: '#111827',
            fontFamily: maerskFontFamily,
            textAlign: 'left'
          }}
        >
          Total {incidentLabelMap[activeTab] || activeTab.toUpperCase()} Incidents: {totalForTab}
        </Typography>

        <Box sx={{ maxHeight: '65vh', overflowY: 'auto', pr: 1 }}>
          {currentIncidents.length ? (
            currentIncidents.map((incident, index) => (
              <IncidentCard key={`${activeTab}-${incident.number || index}`} incident={incident} />
            ))
          ) : (
            <Typography
              sx={{
                fontSize: '0.9rem',
                color: '#6b7280',
                py: 4,
                textAlign: 'center',
                fontFamily: maerskFontFamily
              }}
            >
              No incidents recorded for this severity.
            </Typography>
          )}
        </Box>
      </Box>
      <McButton slot="footer" dialogaction="close" onClick={onClose}>
        Close
      </McButton>
    </McDrawer>
  );
};

export default IncidentsOverviewDrawer;
